import { motion } from 'framer-motion'
import { Linkedin, Quote } from 'lucide-react'
import type { TeamMember } from '../../data/team'
import { SafeImage } from '../ui/SafeImage'

interface TeamCardProps {
  member: TeamMember
  delay?: number
  /** Featured founders use contain; rest of team uses cover + top */
  imageFit?: 'contain' | 'cover'
}

export function TeamCard({ member, delay = 0, imageFit = 'cover' }: TeamCardProps) {
  return (
    <motion.div
      className="group relative overflow-hidden rounded-2xl card-glass p-0 transition-all duration-300 hover:shadow-card-hover dark:hover:shadow-card-hover flex flex-col"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -4 }}
    >
      <div className="relative flex flex-col flex-1 min-h-0">
        <div className="relative h-52 overflow-hidden bg-slate-100 sm:h-56 dark:bg-slate-800">
          <SafeImage
            src={member.image}
            alt={`${member.name} – ${member.role} at DigiSutra`}
            label={`${member.name} — ${member.role}`}
            tone="default"
            className={
              imageFit === 'contain'
                ? 'absolute inset-0 h-full w-full object-cover object-[center_top] scale-[1.3] origin-[center_top]'
                : 'absolute inset-0 h-full w-full object-cover object-top'
            }
            loading="lazy"
            width={300}
            height={224}
          />
        </div>
        <div className="p-5 sm:p-6 flex flex-col flex-1 min-h-0">
          {member.bio && (
            <Quote className="absolute top-4 right-4 w-8 h-8 text-primary/20 dark:text-accent/20 hidden sm:block" />
          )}
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-base sm:text-lg">{member.name}</h3>
          <p className="mt-1 text-accent font-medium text-sm">{member.role}</p>
          {member.bio ? (
            <p className="mt-3 text-slate-600 dark:text-slate-400 text-sm line-clamp-3 flex-1">{member.bio}</p>
          ) : (
            <div className="flex-1 min-h-[3.75rem]" />
          )}
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto pt-4 inline-flex items-center gap-2 text-slate-500 hover:text-primary dark:hover:text-accent text-sm transition-colors w-fit"
              aria-label={`${member.name} on LinkedIn`}
            >
              <Linkedin size={18} className="shrink-0" /> Connect
            </a>
          )}
        </div>
      </div>
    </motion.div>
  )
}
